import React from 'react';
import {
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Info,
  Shield,
  PartyPopper,
  ExternalLink,
  Trash2,
} from 'lucide-react';
import { Notification } from '@/types';

interface NotificationItemProps {
  notification: Notification;
  onMarkRead?: (id: string) => void;
  onDelete?: (id: string) => void;
  onClick?: (notification: Notification) => void;
  showActions?: boolean;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkRead,
  onDelete,
  onClick,
  showActions = true,
}) => {
  const getNotificationIcon = (type: Notification['type']) => {
    const iconClass = 'w-5 h-5';

    switch (type) {
      case 'success':
        return <CheckCircle className={`${iconClass} text-green-500`} />;
      case 'error':
        return <AlertCircle className={`${iconClass} text-red-500`} />;
      case 'warning':
        return <AlertTriangle className={`${iconClass} text-yellow-500`} />;
      case 'security':
        return <Shield className={`${iconClass} text-red-600`} />;
      case 'welcome':
        return <PartyPopper className={`${iconClass} text-purple-500`} />;
      case 'verification':
        return <CheckCircle className={`${iconClass} text-brand-primary`} />;
      case 'info':
      default:
        return <Info className={`${iconClass} text-brand-primary`} />;
    }
  };

  const getNotificationBgColor = (type: Notification['type']) => {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'security':
        return 'bg-red-50 border-red-300';
      case 'welcome':
        return 'bg-purple-50 border-purple-200';
      case 'verification':
        return 'bg-brand-50 border-brand-200';
      case 'info':
      default:
        return 'bg-brand-50 border-brand-200';
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;

    return date.toLocaleDateString();
  };

  const handleClick = () => {
    if (onClick) {
      onClick(notification);
    } else if (notification.actionUrl) {
      // Navigate to action URL
      window.location.href = notification.actionUrl;
    }

    // Mark as read when clicked
    if (!notification.isRead && onMarkRead) {
      onMarkRead(notification._id);
    }
  };

  const handleMarkRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onMarkRead) {
      onMarkRead(notification._id);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(notification._id);
    }
  };

  return (
    <div
      className={`relative border rounded-lg p-4 transition-all duration-200 hover:shadow-md cursor-pointer group ${
        notification.isRead
          ? 'bg-white border-gray-200'
          : `${getNotificationBgColor(notification.type)} shadow-sm`
      }`}
      onClick={handleClick}
    >
      {/* Unread indicator */}
      {!notification.isRead && (
        <div className="absolute top-3 left-2 w-2 h-2 bg-brand-primary rounded-full"></div>
      )}

      <div className="flex items-start space-x-3">
        {/* Icon */}
        <div className="flex-shrink-0 mt-1">{getNotificationIcon(notification.type)}</div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h4
                className={`text-sm font-medium ${
                  notification.isRead ? 'text-gray-700' : 'text-gray-900'
                }`}
              >
                {notification.title}
              </h4>
              <p
                className={`mt-1 text-sm ${
                  notification.isRead ? 'text-gray-500' : 'text-gray-700'
                }`}
              >
                {notification.message}
              </p>

              {/* Action button */}
              {notification.actionUrl && notification.actionText && (
                <div className="mt-2">
                  <span className="inline-flex items-center text-xs font-medium text-brand-primary hover:text-brand-800">
                    {notification.actionText}
                    <ExternalLink className="ml-1 w-3 h-3" />
                  </span>
                </div>
              )}
            </div>

            {/* Actions */}
            {showActions && (
              <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {!notification.isRead && (
                  <button
                    onClick={handleMarkRead}
                    className="p-1 text-gray-400 hover:text-brand-primary rounded"
                    title="Mark as read"
                  >
                    <CheckCircle className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={handleDelete}
                  className="p-1 text-gray-400 hover:text-red-600 rounded"
                  title="Delete notification"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          {/* Timestamp */}
          <div className="mt-2 text-xs text-gray-400">{formatTimeAgo(notification.createdAt)}</div>
        </div>
      </div>
    </div>
  );
};

export default NotificationItem;
